import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton,
  BrutalCard,
  BrutalInput,
  BrutalLabel,
  BrutalTextarea,
  Badge,
  SectionTitle,
} from "../../components/common/Brutal";

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", priority: "info" });

  const load = () => api.get("/announcements").then(({ data }) => setItems(data));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.message) return toast.error("Fill in all fields");
    await api.post("/announcements", form);
    toast.success("Posted!");
    setForm({ title: "", message: "", priority: "info" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/announcements/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-announcements">
      <SectionTitle kicker="Broadcast" title="Announcements" />

      <BrutalCard className="p-5">
        <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <BrutalLabel>Title</BrutalLabel>
            <BrutalInput
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="announcement-title"
            />
          </div>
          <div>
            <BrutalLabel>Priority</BrutalLabel>
            <select
              className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 font-medium"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              data-testid="announcement-priority"
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="sm:col-span-3">
            <BrutalLabel>Message</BrutalLabel>
            <BrutalTextarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="announcement-message"
            />
          </div>
          <div className="sm:col-span-3">
            <BrutalButton variant="primary" type="submit" data-testid="announcement-submit">
              <Plus className="h-4 w-4" /> Post Announcement
            </BrutalButton>
          </div>
        </form>
      </BrutalCard>

      <div className="space-y-3">
        {items.map((a) => (
          <BrutalCard key={a.id} className="p-4" data-testid={`announcement-row-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="bg-[#FFB400] kf-border h-7 w-7 grid place-items-center">
                    <Megaphone className="h-3.5 w-3.5" strokeWidth={2.75} />
                  </div>
                  <Badge
                    color={
                      a.priority === "urgent"
                        ? "red"
                        : a.priority === "warning"
                          ? "amber"
                          : "yellow"
                    }
                  >
                    {a.priority}
                  </Badge>
                  <span className="text-[10px] uppercase tracking-widest text-[#6B4E9E]">
                    {new Date(a.created_at).toLocaleString()}
                  </span>
                </div>
                <h4 className="display text-xl mt-2">{a.title}</h4>
                <p className="text-sm text-[#6B4E9E]">{a.message}</p>
              </div>
              <BrutalButton
                variant="dark"
                className="px-3 py-2 text-xs"
                onClick={() => remove(a.id)}
                data-testid={`announcement-delete-${a.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </BrutalButton>
            </div>
          </BrutalCard>
        ))}
      </div>
    </div>
  );
}
